import { FC, useEffect, useState } from 'react';
import { Box, VStack, HStack, Text, Badge, Button } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogActionTrigger,
} from '@/components/ui/dialog';
import { BirthRecord, Dog } from '@/types/dog';
import { DogIdentityLabel } from '@/components/dog/DogIdentityLabel';
import { dogService } from '@/services/firebase/dogService';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface BirthRecordCardProps {
  birthRecord: BirthRecord;
  userId: string;
  onEdit?: (recordId: string) => void;
  onDelete?: (recordId: string) => void;
}

/**
 * 出産記録カードコンポーネント
 */
export const BirthRecordCard: FC<BirthRecordCardProps> = ({
  birthRecord,
  userId,
  onEdit,
  onDelete,
}) => {
  const [mother, setMother] = useState<Dog | null>(null);
  const [father, setFather] = useState<Dog | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchParents = async () => {
      try {
        const [motherData, fatherData] = await Promise.all([
          dogService.getDog(birthRecord.motherId, userId),
          dogService.getDog(birthRecord.fatherId, userId),
        ]);

        setMother(motherData);
        setFather(fatherData);
      } catch (error) {
        console.error('親犬情報取得エラー:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchParents();
  }, [birthRecord, userId]);

  /**
   * 分娩方法のラベル
   */
  const getDeliveryTypeLabel = (): string => {
    switch (birthRecord.deliveryType) {
      case 'normal':
        return '自然分娩';
      case 'cesarean':
        return '帝王切開';
      case 'other':
        return 'その他';
      default:
        return '不明';
    }
  };

  /**
   * 分娩方法のカラー
   */
  const getDeliveryTypeColor = (): string => {
    switch (birthRecord.deliveryType) {
      case 'normal':
        return 'green';
      case 'cesarean':
        return 'orange';
      case 'other':
        return 'gray';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <Box p={4} borderRadius="lg" borderWidth={1} bg="white" shadow="sm">
        <Text color="gray.500" fontSize="sm">
          読み込み中...
        </Text>
      </Box>
    );
  }

  return (
    <Box
      p={5}
      borderRadius="lg"
      borderWidth={1}
      bg="white"
      shadow="sm"
      transition="all 0.2s"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
    >
      <VStack align="stretch" gap={3}>
        {/* ヘッダー */}
        <HStack justify="space-between">
          <Text fontWeight="bold" fontSize="lg">
            {format(birthRecord.birthDate.toDate(), 'yyyy年M月d日', { locale: ja })}
          </Text>
          <Badge colorPalette={getDeliveryTypeColor()} fontSize="xs">
            {getDeliveryTypeLabel()}
          </Badge>
        </HStack>

        {/* 親犬情報 */}
        <Box>
          <HStack gap={4} align="flex-start">
            <Box flex={1} minW={0}>
              <Text fontSize="xs" color="gray.600">
                母犬
              </Text>
              {mother ? (
                <DogIdentityLabel dog={mother} size="sm" showRegistrationNumbers />
              ) : (
                <Text fontWeight="medium">不明</Text>
              )}
            </Box>
            <Text color="gray.400">×</Text>
            <Box flex={1} minW={0}>
              <Text fontSize="xs" color="gray.600">
                父犬
              </Text>
              {father ? (
                <DogIdentityLabel dog={father} size="sm" showRegistrationNumbers />
              ) : (
                <Text fontWeight="medium">不明</Text>
              )}
            </Box>
          </HStack>
        </Box>

        {/* 頭数情報 */}
        <HStack gap={4} pt={2} borderTopWidth={1}>
          <Box>
            <Text fontSize="xs" color="gray.600">
              総頭数
            </Text>
            <Text fontWeight="bold" fontSize="xl">
              {birthRecord.totalPuppies}頭
            </Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.600">
              生存
            </Text>
            <Text fontWeight="bold" fontSize="xl" color="green.600">
              {birthRecord.alivePuppies}頭
            </Text>
          </Box>
        </HStack>

        {/* 備考 */}
        {birthRecord.notes && (
          <Box pt={2} borderTopWidth={1}>
            <Text fontSize="xs" color="gray.600" mb={1}>
              備考
            </Text>
            <Text fontSize="sm" color="gray.700">
              {birthRecord.notes}
            </Text>
          </Box>
        )}

        {/* アクションボタン */}
        {(onEdit || onDelete) && (
          <HStack gap={2} pt={2}>
            {onEdit && (
              <Button size="sm" variant="outline" onClick={() => onEdit(birthRecord.id)} flex={1}>
                編集
              </Button>
            )}
            {onDelete && (
              <DialogRoot
                open={isDeleteDialogOpen}
                onOpenChange={(e) => setIsDeleteDialogOpen(e.open)}
              >
                <DialogTrigger asChild>
                  <Button size="sm" colorPalette="red" variant="outline" flex={1}>
                    削除
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>出産記録の削除</DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <Text>
                      {format(birthRecord.birthDate.toDate(), 'yyyy年M月d日', { locale: ja })}
                      の出産記録を削除してもよろしいですか？
                    </Text>
                    <Text mt={2} fontSize="sm" color="red.600">
                      この操作は取り消せません。
                    </Text>
                  </DialogBody>
                  <DialogFooter>
                    <DialogActionTrigger asChild>
                      <Button variant="outline">キャンセル</Button>
                    </DialogActionTrigger>
                    <Button
                      colorPalette="red"
                      onClick={() => {
                        onDelete(birthRecord.id);
                        setIsDeleteDialogOpen(false);
                      }}
                    >
                      削除
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </DialogRoot>
            )}
          </HStack>
        )}
      </VStack>
    </Box>
  );
};
